import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FreedomPayMessage, FreedomPayMessageType, FreedomPayService, ProviderActionModel, ProviderName } from '@fgb/core';
import { BaseExpressCheckoutComponent, EXPRESS_CHECKOUT_SETTINGS, ExpressCheckoutSettings } from '../../base-express-checkout';
import { TranslateModule } from '@ngx-translate/core';

/**
 * FreedomPay Hosted Payment Component (HPC) express checkout provider. Loads the HPC SDK, requests
 * a session, embeds the returned iframe, and listens for the iframe's postMessage protocol to
 * resize the frame and capture the payment SessionKeys. On capture it posts them to the server and
 * emits the result up to the ExpressCheckoutComponent container.
 *
 * Note: unlike a one-tap wallet, HPC renders a card-entry iframe immediately rather than on a
 * button press, so it initialises in ngOnInit rather than waiting for a submit().
 */
@Component({
  selector: 'fgbcl-freedom-pay',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './freedom-pay.component.html',
  styleUrl: './freedom-pay.component.scss',
  providers: [
    {
      provide: EXPRESS_CHECKOUT_SETTINGS,
      useValue: (settings: any): ExpressCheckoutSettings => ({
        settings: {
          sessionKey: settings.sessionKey,
          iframeHtml: settings.url,
        },
      }),
    },
  ],
})
export class FreedomPayComponent extends BaseExpressCheckoutComponent implements OnInit, OnDestroy {
  @ViewChild('paymentFrame') paymentFrame?: ElementRef<HTMLDivElement>;

  private freedomPayService = inject(FreedomPayService);
  private sanitizer = inject(DomSanitizer);
  private zone = inject(NgZone);
  private cdr = inject(ChangeDetectorRef);

  /** Sanitised iframe markup returned by FreedomPay. */
  iframeHtml: SafeHtml | null = null;
  loading = true;

  /** Origin of the embedded HPC iframe; postMessages from any other origin are ignored. */
  private expectedOrigin: string | null = null;

  private messageHandler = (event: MessageEvent) => this.onMessage(event);

  constructor() {
    super(ProviderName.FreedomPay, inject(FreedomPayService));
  }

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  async ngOnInit(): Promise<void> {
    // Listen outside Angular; we re-enter the zone only when something meaningful happens.
    this.zone.runOutsideAngular(() => window.addEventListener('message', this.messageHandler));
    // The base drives init -> onInitialized(), where the iframe is embedded.
    await super.ngOnInit();
  }

  override ngOnDestroy(): void {
    window.removeEventListener('message', this.messageHandler);
    // The base releases the provider SDK; skipping super here would leak it.
    super.ngOnDestroy();
  }

  /* ================================
   * Post-init hook
   * ================================ */

  /** Embed the HPC iframe the service captured during initialize(). */
  protected override onInitialized(action: ProviderActionModel): void {
    const settings = this.extractSettings(action);
    const markup = settings?.url || '';

    // Pin the message listener to the iframe's origin BEFORE embedding it, so SessionKeys can only
    // ever be accepted from the real HPC frame.
    this.expectedOrigin = this.resolveIframeOrigin(markup);
    if (!this.expectedOrigin) {
      this.logService.error('FreedomPay: could not resolve the HPC iframe origin; ignoring all frame messages');
    }

    this.iframeHtml = this.sanitizer.bypassSecurityTrustHtml(markup);
    this.loading = false;
    // Dynamically-created component + async data: force the view to pick up the iframe.
    this.cdr.detectChanges();
  }

  /** Init failed or the SDK refused to start: clear the pending UI and tell the host to fall back. */
  protected override onInitializeFailed(): void {
    this.loading = false;
    this.incomplete.emit({ providerId: ProviderName.FreedomPay, success: false });
    this.cdr.detectChanges();
  }

  /* ================================
   * Private Helpers
   * ================================ */

  private onMessage(event: MessageEvent): void {
    // Only trust messages that come from the embedded HPC iframe itself: both the origin and the
    // source window must match. Anything else on the page could otherwise inject SessionKeys.
    if (!this.expectedOrigin || event.origin !== this.expectedOrigin || !this.isFromPaymentFrame(event.source)) {
      return;
    }

    const message = event.data as FreedomPayMessage;
    if (!message || typeof message.type !== 'number') {
      return;
    }

    switch (message.type) {
      case FreedomPayMessageType.Resize:
        this.resizeFrame(message.data?.height);
        break;
      case FreedomPayMessageType.SessionKeys:
        this.captureSessionKeys(message.data);
        break;
      case FreedomPayMessageType.Ready:
      case FreedomPayMessageType.Complete:
      default:
        break;
    }
  }

  private resizeFrame(height?: number): void {
    if (!height || !this.paymentFrame) return;
    // Size the actual iframe to the content height reported by the HPC frame.
    const iframe = this.paymentFrame.nativeElement.querySelector('iframe');
    if (iframe) {
      iframe.style.height = `${height}px`;
    }
  }

  private async captureSessionKeys(data: unknown): Promise<void> {
    const sessionKeys = JSON.stringify(data);
    const sessionKey = this.config?.settings?.sessionKey || '';

    const submitted = await this.freedomPayService.submitSessionKeys(sessionKeys, this.context, sessionKey);

    // Re-enter Angular to emit, since the message arrived outside the zone.
    this.zone.run(() =>
      this.result.emit({
        providerId: ProviderName.FreedomPay,
        success: submitted,
        data: this.context.orderId,
      })
    );
  }

  /** Whether a message event's source window is the embedded HPC iframe. */
  private isFromPaymentFrame(source: MessageEventSource | null): boolean {
    const iframe = this.paymentFrame?.nativeElement.querySelector('iframe');
    return !!iframe?.contentWindow && source === iframe.contentWindow;
  }

  /**
   * Parse the iframe src out of the HPC markup (without attaching it to the DOM) and return its
   * origin. Returns null when the markup has no https iframe src, which disables message handling.
   */
  private resolveIframeOrigin(markup: string): string | null {
    if (!markup) {
      return null;
    }
    try {
      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const src = doc.querySelector('iframe')?.getAttribute('src') ?? '';
      const origin = src ? new URL(src, window.location.href).origin : null;
      // The hosted payment frame is always served from an https FreedomPay host.
      return origin?.startsWith('https://') ? origin : null;
    } catch {
      return null;
    }
  }
}
